import { useEffect, useState } from "react";
import { useLang } from "../i18n";
import { api, formatApiError } from "../lib/api";

const emptyDress = {
  code: "", name: "", designer: "Per Lei Couture", silhouette: "A-Line", fabric: "Crepe",
  category: "bridal", collection: "selected", rental_price: "", purchase_price: "",
  lead_time: "2-6 mesi", description: "", images: "", sizes: "40, 42, 44", status: "available", featured: false,
};

const Login = ({ onLogin }) => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("pl_admin_token", data.access_token);
      onLogin(data);
    } catch (err) {
      setError(err.response?.status === 401 ? "Email o password errata." : (formatApiError(err.response?.data?.detail) || "Errore di connessione. Riprova tra poco."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] text-ivory flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6">
        <img src="/logo-monogram-white.png" alt="Per Lei Bridal" className="h-16 w-auto" draggable="false" />
        <p className="eyebrow !text-ivory/40">Per Lei — Atelier</p>
        <h1 className="font-serif text-4xl">{t("admin.title")}</h1>
        <input className="field !border-ivory/20 !text-ivory" data-testid="admin-login-email" type="email" placeholder={t("admin.email")}
          value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className="field !border-ivory/20 !text-ivory" data-testid="admin-login-password" type="password" placeholder={t("admin.password")}
          value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
        <button className="btn-solid !bg-ivory !text-charcoal hover:!bg-gold w-full" data-testid="admin-login-submit" disabled={busy}>
          {busy ? "…" : t("admin.login")}
        </button>
      </form>
    </div>
  );
};

const DressForm = ({ initial, onSave, onCancel }) => {
  const { t } = useLang();
  const [d, setD] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const set = (k, v) => setD({ ...d, [k]: v });

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setUploadError("");
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      const current = (Array.isArray(d.images) ? d.images : String(d.images || "").split(","))
        .map((s) => s.trim()).filter(Boolean);
      setD({ ...d, images: [...current, ...urls].join(", ") });
    } catch (err) {
      setUploadError(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (url) => setD({ ...d, images: d.images.split(",").map((s) => s.trim()).filter((s) => s && s !== url).join(", ") });

  const payload = {
    ...d,
    rental_price: d.rental_price ? parseInt(d.rental_price) : null,
    purchase_price: d.purchase_price ? parseInt(d.purchase_price) : null,
    images: (Array.isArray(d.images) ? d.images : String(d.images || "").split(",")).map((s) => s.trim()).filter(Boolean),
    sizes: d.sizes.split(",").map((s) => s.trim()).filter(Boolean),
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(payload); }} className="bg-[#1E1E1E] p-6 rounded-sm space-y-4" data-testid="admin-dress-form">
      <div className="grid sm:grid-cols-3 gap-4">
        <input className="field !border-ivory/20 !text-ivory" required placeholder="Codice (PL-…)" data-testid="admin-field-code" value={d.code} onChange={(e) => set("code", e.target.value)} />
        <input className="field !border-ivory/20 !text-ivory" required placeholder="Nome" data-testid="admin-field-name" value={d.name} onChange={(e) => set("name", e.target.value)} />
        <input className="field !border-ivory/20 !text-ivory" required placeholder="Designer" data-testid="admin-field-designer" value={d.designer} onChange={(e) => set("designer", e.target.value)} />
      </div>
      <div className="grid sm:grid-cols-5 gap-4">
        {["silhouette", "fabric", "category", "collection", "status"].map((k) => {
          const opts = {
            silhouette: ["A-Line", "Ballgown", "Mermaid", "Sheath"],
            fabric: ["Satin", "Lace", "Tulle", "Mikado", "Organza"],
            category: ["bridal", "evening", "suits", "mother"],
            collection: ["selected", "couture"],
            status: ["available", "reserved", "rented", "archive"],
          }[k];
          return (
            <select key={k} className="field !border-ivory/20 !text-ivory" data-testid={`admin-field-${k}`} value={d[k]}
              onChange={(e) => set(k, e.target.value)}>
              {(k === "silhouette" || k === "fabric") && <option value="">—</option>}
              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          );
        })}
      </div>
      <div className="grid sm:grid-cols-4 gap-4">
        <input className="field !border-ivory/20 !text-ivory" placeholder="Noleggio €" data-testid="admin-field-rental" value={d.rental_price} onChange={(e) => set("rental_price", e.target.value)} />
        <input className="field !border-ivory/20 !text-ivory" placeholder="Acquisto €" data-testid="admin-field-purchase" value={d.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} />
        <input className="field !border-ivory/20 !text-ivory" placeholder="Lead time" data-testid="admin-field-lead" value={d.lead_time} onChange={(e) => set("lead_time", e.target.value)} />
        <input className="field !border-ivory/20 !text-ivory" placeholder="Taglie (40, 42)" data-testid="admin-field-sizes" value={d.sizes} onChange={(e) => set("sizes", e.target.value)} />
      </div>
      <div data-testid="admin-photo-upload">
        <label className="btn-ghost !border-ivory/30 !text-ivory cursor-pointer" data-testid="admin-photo-input-label">
          {uploading ? "…" : "+ Carica foto"}
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
            data-testid="admin-field-photos" onChange={(e) => uploadFiles([...e.target.files])} />
        </label>
        {uploadError && <p className="text-sm text-red-400 mt-2">{uploadError}</p>}
      {(() => {
        const imageList = (Array.isArray(d.images) ? d.images : String(d.images || "").split(",")).map((s) => s.trim()).filter(Boolean);
        return imageList.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-4">
            {imageList.map((img, idx) => (
              <div key={`${idx}-${img.slice(-20)}`} className="relative w-16 h-20 bg-ivory/5 border border-ivory/20 overflow-hidden group/thumb">
                <img src={img} alt="" className="w-full h-full object-cover" />
                {idx === 0 && <span className="absolute bottom-0 inset-x-0 bg-charcoal/80 text-ivory text-[8px] tracking-[0.2em] uppercase text-center py-0.5">{t("admin.cover")}</span>}
                <div className="absolute inset-x-0 top-0 flex justify-between opacity-0 group-hover/thumb:opacity-100 transition-opacity">
                  <button type="button" data-testid={`admin-photo-left-${idx}`} disabled={idx === 0}
                    className="bg-charcoal/80 text-ivory text-xs px-1.5 disabled:opacity-25"
                    onClick={() => { const n = [...imageList]; [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]]; setD({ ...d, images: n.join(", ") }); }}>‹</button>
                  <button type="button" data-testid={`admin-photo-remove-${idx}`}
                    className="bg-charcoal/80 text-ivory text-xs px-1.5"
                    onClick={() => removeImage(img)}>×</button>
                  <button type="button" data-testid={`admin-photo-right-${idx}`} disabled={idx === imageList.length - 1}
                    className="bg-charcoal/80 text-ivory text-xs px-1.5 disabled:opacity-25"
                    onClick={() => { const n = [...imageList]; [n[idx + 1], n[idx]] = [n[idx], n[idx + 1]]; setD({ ...d, images: n.join(", ") }); }}>›</button>
                </div>
              </div>
            ))}
          </div>
        );
      })()}
      </div>
      <input className="field !border-ivory/20 !text-ivory" placeholder="…oppure incolla URL foto (separati da virgola)" data-testid="admin-field-images" value={d.images} onChange={(e) => set("images", e.target.value)} />
      <textarea className="field !border-ivory/20 !text-ivory" rows="2" placeholder="Descrizione" data-testid="admin-field-description" value={d.description} onChange={(e) => set("description", e.target.value)} />
      <label className="flex items-center gap-3 text-sm text-ivory/70" data-testid="admin-field-featured">
        <input type="checkbox" checked={d.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured
      </label>
      <div className="flex gap-4 pt-2">
        <button className="btn-solid !bg-ivory !text-charcoal hover:!bg-gold" data-testid="admin-dress-save">{t("admin.save")}</button>
        <button type="button" className="btn-ghost !border-ivory/30 !text-ivory" data-testid="admin-dress-cancel" onClick={onCancel}>✕</button>
      </div>
    </form>
  );
};

const BulkPhotos = ({ dresses, loadDresses, t, setNotice }) => {
  const [pool, setPool] = useState([]);
  const [target, setTarget] = useState({});
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const uploadMany = async (files) => {
    if (!files?.length) return;
    setUploading(true); setError("");
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setPool((p) => [...p, ...urls]);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const assign = async (url) => {
    const id = target[url];
    const dress = dresses.find((x) => x.id === id);
    if (!dress) return;
    setBusy(url);
    try {
      await api.put(`/admin/dresses/${dress.id}`, { ...dress, images: [...(dress.images || []), url] });
      setPool((p) => p.filter((u) => u !== url));
      loadDresses();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-bulk-photos">
      <p className="text-sm text-ivory/50 mb-4">{t("admin.bulkHint")}</p>
      <label className="btn-solid !bg-ivory !text-charcoal hover:!bg-gold cursor-pointer" data-testid="admin-bulk-upload-label">
        {uploading ? "…" : "+ " + t("admin.photos")}
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden data-testid="admin-bulk-input"
          onChange={(e) => uploadMany([...e.target.files])} />
      </label>
      {error && <p className="text-sm text-red-400 mt-3">{error}</p>}
      {pool.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mt-6">
          {pool.map((url, i) => (
            <div key={url} className="bg-[#1E1E1E] p-2 rounded-sm" data-testid={`admin-bulk-item-${i}`}>
              <div className="aspect-[3/4] bg-ivory/5 overflow-hidden">
                <img src={url} alt="" className="w-full h-full object-cover" />
              </div>
              <select className="w-full bg-transparent border border-ivory/20 mt-2 py-1.5 text-xs" data-testid={`admin-bulk-select-${i}`}
                value={target[url] || ""} onChange={(e) => setTarget({ ...target, [url]: e.target.value })}>
                <option value="" className="text-charcoal">{t("admin.choose")}</option>
                {dresses.map((d) => <option key={d.id} value={d.id} className="text-charcoal">{d.code} — {d.name}</option>)}
              </select>
              <button className="w-full mt-2 text-[10px] tracking-[0.2em] uppercase border border-gold text-gold py-2 hover:bg-gold hover:text-charcoal transition-colors disabled:opacity-40"
                data-testid={`admin-bulk-assign-${i}`} disabled={!target[url] || busy === url}
                onClick={() => assign(url)}>
                {busy === url ? "…" : t("admin.assign")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const { t } = useLang();
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("dresses");
  const [dresses, setDresses] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | dress
  const [notice, setNotice] = useState("");

  const loadDresses = () => api.get("/dresses").then((r) => setDresses(r.data)).catch(() => {});
  const logout = async () => {
    localStorage.removeItem("pl_admin_token");
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };
  const loadInquiries = () => api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  useEffect(() => {
    if (user) { loadDresses(); loadInquiries(); }
  }, [user]);

  if (user === undefined) return <div className="min-h-screen bg-[#121212]" />;
  if (user === false) return <Login onLogin={(u) => setUser(u)} />;

  const filtered = dresses.filter((d) =>
    (!statusFilter || d.status === statusFilter) &&
    (!search || `${d.code} ${d.name} ${d.designer}`.toLowerCase().includes(search.toLowerCase()))
  );

  const saveDress = async (payload) => {
    try {
      if (editing === "new") await api.post("/admin/dresses", payload);
      else await api.put(`/admin/dresses/${editing.id}`, payload);
      setEditing(null); setNotice("Salvato ✓"); loadDresses();
    } catch (err) {
      setNotice(formatApiError(err.response?.data?.detail));
    }
  };

  const removeDress = async (d) => {
    if (!window.confirm(`Eliminare ${d.name}?`)) return;
    await api.delete(`/admin/dresses/${d.id}`);
    loadDresses();
  };

  return (
    <div className="min-h-screen bg-[#121212] text-ivory" data-testid="admin-dashboard">
      <header className="border-b border-ivory/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-serif text-xl">{t("admin.title")}</p>
          <div className="flex items-center gap-6">
            <button onClick={() => setTab("dresses")} data-testid="admin-tab-dresses"
              className={`text-[11px] tracking-[0.2em] uppercase ${tab === "dresses" ? "text-gold" : "text-ivory/50 hover:text-ivory"}`}>{t("admin.dresses")} ({dresses.length})</button>
            <button onClick={() => setTab("photos")} data-testid="admin-tab-photos"
              className={`text-[11px] tracking-[0.2em] uppercase ${tab === "photos" ? "text-gold" : "text-ivory/50 hover:text-ivory"}`}>{t("admin.photos")}</button>
            <button onClick={() => setTab("inquiries")} data-testid="admin-tab-inquiries"
              className={`text-[11px] tracking-[0.2em] uppercase ${tab === "inquiries" ? "text-gold" : "text-ivory/50 hover:text-ivory"}`}>{t("admin.inquiries")} ({inquiries.filter((i) => i.status === "new").length})</button>
            <button onClick={logout} data-testid="admin-logout"
              className="text-[11px] tracking-[0.2em] uppercase text-ivory/50 hover:text-ivory">{t("admin.logout")}</button>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        {notice && <p className="text-sm text-gold mb-4" data-testid="admin-notice">{notice}</p>}

        {tab === "dresses" && (
          <>
            <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
              <div className="flex gap-3 items-center">
                <input className="field !border-ivory/20 !text-ivory !py-2 w-64" data-testid="admin-search" placeholder={t("admin.search")}
                  value={search} onChange={(e) => setSearch(e.target.value)} />
                <select className="field !border-ivory/20 !text-ivory !py-2 w-40" data-testid="admin-status-filter"
                  value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">{t("admin.status")}: tutti</option>
                  {["available", "reserved", "rented", "archive"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <button className="btn-solid !bg-ivory !text-charcoal hover:!bg-gold !py-3" data-testid="admin-add-dress" onClick={() => setEditing("new")}>
                + {t("admin.add")}
              </button>
            </div>
            {editing && (
              <div className="mb-8">
                <DressForm initial={editing === "new" ? { ...emptyDress } : { ...editing, rental_price: editing.rental_price || "", purchase_price: editing.purchase_price || "", images: editing.images?.join(", ") || "", sizes: editing.sizes?.join(", ") || "" }}
                  onSave={saveDress} onCancel={() => setEditing(null)} />
              </div>
            )}
            <div className="overflow-x-auto" data-testid="admin-dresses-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-ivory/40 border-b border-ivory/10">
                    <th className="py-3 pr-4">Codice</th><th className="pr-4">Nome</th><th className="pr-4">Designer</th>
                    <th className="pr-4">Silh.</th><th className="pr-4">Tessuto</th><th className="pr-4">Cat.</th>
                    <th className="pr-4">Noleggio</th><th className="pr-4">Acquisto</th><th className="pr-4">{t("admin.status")}</th><th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => (
                    <tr key={d.id} className="border-b border-ivory/5 hover:bg-ivory/5" data-testid={`admin-row-${d.code}`}>
                      <td className="py-3 pr-4 text-ivory/60">{d.code}</td>
                      <td className="pr-4 font-serif text-base">{d.name}{d.featured ? " ★" : ""}</td>
                      <td className="pr-4 text-ivory/70">{d.designer}</td>
                      <td className="pr-4 text-ivory/60">{d.silhouette}</td>
                      <td className="pr-4 text-ivory/60">{d.fabric}</td>
                      <td className="pr-4 text-ivory/60">{d.category}</td>
                      <td className="pr-4 text-ivory/60">{d.rental_price ? `€${d.rental_price}` : "—"}</td>
                      <td className="pr-4 text-ivory/60">{d.purchase_price ? `€${d.purchase_price}` : "—"}</td>
                      <td className="pr-4">
                        <select className="bg-transparent border border-ivory/20 rounded-full px-3 py-1 text-xs" data-testid={`admin-status-${d.code}`}
                          value={d.status}
                          onChange={async (e) => { await api.put(`/admin/dresses/${d.id}`, { ...d, images: d.images, sizes: d.sizes, status: e.target.value }); loadDresses(); }}>
                          {["available", "reserved", "rented", "archive"].map((s) => <option key={s} value={s} className="text-charcoal">{s}</option>)}
                        </select>
                      </td>
                      <td className="pr-2 whitespace-nowrap">
                        <button className="text-ivory/60 hover:text-gold mr-4 text-xs uppercase tracking-wider" data-testid={`admin-edit-${d.code}`} onClick={() => setEditing(d)}>{t("admin.edit")}</button>
                        <button className="text-red-400/70 hover:text-red-400 text-xs uppercase tracking-wider" data-testid={`admin-delete-${d.code}`} onClick={() => removeDress(d)}>{t("admin.del")}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && <p className="py-10 text-ivory/40 text-sm">—</p>}
            </div>
          </>
        )}

        {tab === "photos" && (
          <BulkPhotos dresses={dresses} loadDresses={loadDresses} t={t} setNotice={setNotice} />
        )}

        {tab === "inquiries" && (
          <div className="space-y-4" data-testid="admin-inquiries-list">
            {inquiries.map((inq) => (
              <div key={inq.id} className="bg-[#1E1E1E] p-6 rounded-sm" data-testid={`admin-inquiry-${inq.id}`}>
                <div className="flex flex-wrap justify-between gap-4 items-start">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-gold">{inq.type} · {new Date(inq.created_at).toLocaleString("it-IT")}</p>
                    <p className="font-serif text-xl mt-1">{inq.name} <span className="text-sm text-ivory/50">— {inq.email} {inq.phone ? `· ${inq.phone}` : ""}</span></p>
                    {inq.dress_name && <p className="text-sm text-ivory/60 mt-1">Abito: {inq.dress_name}</p>}
                    {inq.appointment_type && <p className="text-sm text-ivory/60">{inq.appointment_type} {inq.preferred_date ? `— ${inq.preferred_date}` : ""}</p>}
                    {inq.message && <p className="text-sm text-ivory/70 mt-2 max-w-2xl">{inq.message}</p>}
                  </div>
                  <select className="bg-transparent border border-ivory/20 rounded-full px-3 py-1 text-xs" data-testid={`admin-inquiry-status-${inq.id}`}
                    value={inq.status}
                    onChange={async (e) => { await api.patch(`/inquiries/${inq.id}`, { status: e.target.value }); loadInquiries(); }}>
                    {["new", "confirmed", "completed", "archived"].map((s) => <option key={s} value={s} className="text-charcoal">{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
            {inquiries.length === 0 && <p className="py-10 text-ivory/40 text-sm">—</p>}
          </div>
        )}
      </main>
    </div>
  );
}
